import CertificateIcon from "@/components/icons/CertificateIcon";
import ScoUserFeedback from "@/components/courses/ScoUserFeedback";
import ScoReport from "@/components/courses/ScoReport";
import ScoCompetencyReport from "@/components/courses/ScoCompetencyReport";
import ScoCertificate from "@/components/courses/ScoCertificate";
import ConfirmModal from "@/components/utilities/ConfirmModal";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

export default {
  components: {
    CertificateIcon,
    ScoUserFeedback,
    ScoReport,
    ScoCompetencyReport,
    ScoCertificate,
    ConfirmModal
  },
  props: {
    sco: {
      type: Object,
      required: true
    },
    slug: {
      type: String,
      required: true
    },
    course: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      confirmModal: {
        visible: false,
        title: "Course Locked",
        message: "",
      }
    }
  },
  computed: {
    lockedOut() {
      //we're adding this to work around a Storyline bug caused when users reopen a course that uses subsets of questions
      //e.g. only the questions they got wrong the first attempt
      //rules: passed 1st or 2nd = locked out. failed 2nd attempt = locked out, course.storyline_subset_bug = true
      if (this.course[0].storyline_subset_bug) {
        if (this.sco.lesson_status === "passed") {
          return true;
        } else if (this.sco.lesson_status === "failed" && this.sco.attempts === 2) {
          return true;
        }
      }

      if (this.sco.manually_overridden != 0) {
        return true;
      }

      return false;
    },
    lockedOutAlt() {
      if (this.course[0].storyline_subset_bug) {
        if (this.sco.lesson_status === "passed") {
          return "You have already passed this course.";
        } else if (this.sco.lesson_status === "failed" && this.sco.attempts === 2) {
          return "You have used both attempts for this course. Please contact your Line Manager.";
        }
      }

      if (this.sco.manually_overridden != 0) {
        if (this.sco.lesson_status === "passed") {
          return `Congratulations! You have already passed this course. 
          You can access your certificate from your Learning Zone by clicking on the 
          certificate icon next to the course title.`;
        } else if (this.sco.lesson_status === "failed") {
          return `Unfortunately you have not achieved the required pass mark for this course. 
          Please contact OPUS Support on the details above to find out your next steps.`;
        }
      }

      return "Open Course";
    },
    statusText() {
      switch (this.sco.lesson_status) {
        case "not attempted”":
          return "Not Attempted";
        case "browsed":
        case "incomplete":
          return "Incomplete";
        case "completed":
          return "Completed";
        case "passed":
          return "Passed";
        case "failed":
          return "More Training Needed";
        default:
          return "Not Attempted";
      }
    },
    statusIconComponentName() {
      switch (this.sco.lesson_status) {
        case "not attempted”":
          return "IconNotStarted";
        case "browsed":
        case "incomplete":
          return "IconStarted";
        case "completed":
          return "IconPassed";
        case "passed":
          return "IconPassed";
        case "failed":
          return "IconFailed";
        default:
          return "IconNotStarted";
      }
    },
    score() {
      switch (this.sco.lesson_status) {
        case "passed":
        case "failed":
          return Math.round(this.sco.raw_score);
        default:
          //the WYA course sends raw_score of 100% after a user has only correctly answered 1 questions
          //so we don't want to score if the lesson status is incomplete
          return null;
      }
    },
    feedbackAllowed() {
      switch (this.sco.lesson_status) {
        case "completed":
          return true;
        default:
          return false;
      }
    },
    reportAllowed() {
      if (this.sco.has_old_style_report) {
        switch (this.sco.lesson_status) {
          case "passed":
          case "failed":
            return true;
          default:
            return false;
        }
      }

      return false;
    },
    competencyReportAllowed() {
      if (this.sco.has_competency_map) {
        switch (this.sco.lesson_status) {
          case "passed":
          case "failed":
            return true;
          default:
            return false;
        }
      }

      return false;
    },
    certificateAllowed() {
      //OPUS decided that some orgs will not want their members to have access to viewing their own certs
      //So, we have the following rules for determining access:
      //the user must have passed
      //if the course was purchase by them as a solo licence they can view the cert
      //if the course was purchased for them by an org, the org must Not have turned off the setting 'members_can_access_certificates'

      switch (this.sco.lesson_status) {
        case "passed":
          if (this.sco.group_id) {
            return this.sco.members_can_access_certificates != 0;
          } else {
            //solo
            return true;
          }
        default:
          return false;
      }
    },
    dateFinished() {
      return dayjs(this.sco.score_recorded_at).format("D-MM-YY");
    },
    notFinished() {
      return (
        this.sco.lesson_status !== "passed" &&
        this.sco.lesson_status !== "failed" &&
        this.sco.lesson_status !== "completed"
      );
    },
    daysLeft() {
      if (this.sco.individual_days_left !== null) {
        return this.sco.individual_days_left;
      }

      return this.sco.days_left;
    },
    postTrainingDaysExpired() {
      if (this.sco.deliverable_id === 3 && !isNaN(this.daysLeft) && this.daysLeft <= 0) {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    contentRoute(sco, slug) {
      if (this.postTrainingDaysExpired) {
        return this.$route;
      } else {
        return { name: "Content", params: { scoUserId: sco.sco_user_id, slug } };
      }
    },
    handleContentClicked() {
      if (this.lockedOut) {
        this.confirmModal.visible = true;
        this.confirmModal.message = this.lockedOutAlt;
        return;
      }

      this.$router.push(this.contentRoute(this.sco, this.slug));
    }
  }
};
